import { AxiosError, type AxiosResponse } from 'axios';
import { useMemo, type FC } from 'react';
import { useFormik } from 'formik';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import { IconLoader2 } from '@tabler/icons-react';

import { ButtonField, Smokebreak } from '@wk/components-react16';

import { fetchApplicationConfigurations } from '@/domains/application/applicationApi';
import type { ApplicationConfiguration } from '@/domains/application/applicationConfiguration.types';

import type {
  ErrorResponse,
  EvaluationStatusResponse,
  TriggerEvaluationRequest,
} from '@/types';
import {
  getEvaluationJobStatus as getEvaluationJobStatusService,
  triggerEvaluation as triggerEvaluationService,
} from '@/domains/evaluation/evaluationApi';

import useOnGoingEvaluationsStore from '@/features/ongoingEvaluations/stores/ongoingEvaluationsStore';
import useToast from '@/shared/stores/toastStore';

import RunEvaluationDialogBody from './RunEvaluationDialogBody';
import type {
  EvaluationJobStatus,
  RunEvaluationProps,
  TriggerEvaluationErrorResponse,
  TriggerEvaluationResponse,
} from './RunEvaluationDialog.types';
import {
  getLatestConfiguration,
  getRunEvaluationDerivedData,
  getRunEvaluationInitialValues,
  getRunEvaluationValidationSchema,
} from './RunEvaluationDialog.utils';

import './RunEvaluationDialog.css';

const RunEvaluation: FC<RunEvaluationProps> = ({
  isOpen,
  onClose,
  application,
}) => {
  const { addOngoingEvaluation, setIsOngoingEvaluationsOpen, updateEvaluations } =
    useOnGoingEvaluationsStore(
      useShallow((state) => ({
        addOngoingEvaluation: state.addOngoingEvaluation,
        setIsOngoingEvaluationsOpen: state.setIsOngoingEvaluationsOpen,
        updateEvaluations: state.updateEvaluations,
      })),
    );

  const { showToast } = useToast(
    useShallow((state) => ({
      showToast: state.showToast,
    })),
  );

  const {
    data: applicationConfigurations,
    isLoading,
    error: applicationConfigurationsError,
  } = useQuery({
    queryKey: ['application-configurations', application?.bitId],
    queryFn: () => fetchApplicationConfigurations(application?.bitId || ''),
    enabled: !!application,
  });

  const appConfigError =
    applicationConfigurationsError as AxiosError<ErrorResponse> | null;

  const latestConfiguration = useMemo(() => {
    return getLatestConfiguration(
      applicationConfigurations as ApplicationConfiguration[] | null | undefined,
    );
  }, [applicationConfigurations]);

  const {
    runtimeParametersByCheckpoint,
    configTimeParametersByCheckpoint,
    parameters,
  } = useMemo(() => {
    return getRunEvaluationDerivedData(latestConfiguration);
  }, [latestConfiguration]);

  const initialValues = useMemo(() => {
    return getRunEvaluationInitialValues(parameters, application);
  }, [application, parameters]);

  const validationSchema = useMemo(() => {
    return getRunEvaluationValidationSchema(parameters);
  }, [parameters]);

  const { mutate: triggerEvaluationMutation, isPending } = useMutation<
    AxiosResponse<TriggerEvaluationResponse>,
    AxiosError<TriggerEvaluationErrorResponse>,
    TriggerEvaluationRequest
  >({
    mutationFn: triggerEvaluationService,
    onSuccess: (data) => {
      addOngoingEvaluation({
        ...data.data,
        applicationName: application?.name || '',
        bitId: application?.bitId || '',
        startedAt: new Date().toISOString(),
        status: 'Running',
        output: null,
        environmentName: values.environment || '',
      });
      setIsOngoingEvaluationsOpen(true);
      onClose();
    },
    onError: async (error) => {
      const errorData = error.response?.data;
      const apiMessage =
        errorData?.message || errorData?.title || 'Something went wrong';
      const statusCode = error.response?.status;
      const isConflict = statusCode === 409 || statusCode === 423;

      if (isConflict) {
        const bitIdentifier = application?.bitId || 'Unknown BIT ID';
        const environmentName = values.environment || 'this environment';
        const statusKey =
          typeof errorData?.status === 'string'
            ? errorData.status.toLowerCase().replace(/[\s_-]/g, '')
            : '';

        const isRecentlyCompleted =
          statusKey === 'recentlycompleted' || Boolean(errorData?.completedAt);

        let normalizedStatus: EvaluationJobStatus = isRecentlyCompleted
          ? 'Completed'
          : statusKey === 'failed'
            ? 'Failed'
            : statusKey === 'pending'
              ? 'Pending'
              : 'Running';

        const conflictMessage =
          apiMessage ||
          `An evaluation for BIT ID ${bitIdentifier} in ${environmentName} is already running. Please wait for it to finish.`;

        const conflictJobId = errorData?.jobId;
        if (conflictJobId) {
          let output: EvaluationStatusResponse['output'] | null = null;
          let message = errorData?.message;

          if (isRecentlyCompleted) {
            try {
              const statusResponse =
                await getEvaluationJobStatusService(conflictJobId);
              normalizedStatus = statusResponse.data?.status ?? normalizedStatus;
              output = statusResponse.data?.output ?? null;
              message = statusResponse.data?.output?.message || message;
            } catch {
              // Keep the conflict payload when the follow-up status lookup fails.
            }
          }

          if (isRecentlyCompleted && !output?.recordId) {
            normalizedStatus = 'Pending';
          }

          const existingEvaluation = useOnGoingEvaluationsStore
            .getState()
            .ongoingEvaluations.find(
              (evaluation) => evaluation.jobId === conflictJobId,
            );

          if (!existingEvaluation) {
            addOngoingEvaluation({
              jobId: conflictJobId,
              statusUrl: errorData?.statusUrl || '',
              bitId: bitIdentifier,
              applicationName: application?.name || '',
              environmentName,
              startedAt:
                errorData?.completedAt ||
                errorData?.createdAt ||
                new Date().toISOString(),
              status: normalizedStatus,
              message,
              output,
            });
          } else {
            updateEvaluations({
              [conflictJobId]: {
                status: normalizedStatus,
                message,
                ...(output ? { output } : {}),
              },
            });
          }
        }

        showToast('info', {
          title: isRecentlyCompleted
            ? 'Evaluation completed recently'
            : 'Evaluation already running',
          message: conflictMessage,
          autoCloseTimeout: 8000,
        });
        setIsOngoingEvaluationsOpen(true);
        return;
      }

      if (error instanceof AxiosError) {
        showToast('error', {
          title: 'Unable to start evaluation',
          message: apiMessage,
        });
      } else {
        showToast('error', {
          title: 'Unable to start evaluation',
          message: 'Unexpected error occurred while starting the evaluation.',
        });
      }
    },
  });

  const { values, handleChange, handleSubmit, isValid } = useFormik<
    Record<string, string | null>
  >({
    initialValues,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: false,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (formValues) => {
      const { environment, ...otherProps } = formValues;
      triggerEvaluationMutation({
        environments: environment || '',
        bitId: application?.bitId || '',
        force: true,
        PassThreshold: 100,
        ...otherProps,
      });
    },
  });

  return (
    <Smokebreak
      isOpen={isOpen}
      position="right"
      onUserRequest={() => onClose()}
    >
      <h2
        id="smokebreak-id-001"
        className="p-3 text-lg font-semibold"
        slot="smokebreakHeader"
      >
        Run Evaluation for {application?.name}
      </h2>
      <form
        className="flex w-full flex-col gap-4 p-3"
        slot="smokebreakBody"
        onSubmit={handleSubmit}
      >
        <div className="flex flex-1 flex-col gap-4">
          <RunEvaluationDialogBody
            isLoading={isLoading}
            latestConfiguration={latestConfiguration}
            application={application}
            appConfigError={appConfigError}
            values={values}
            handleChange={handleChange}
            runtimeParametersByCheckpoint={runtimeParametersByCheckpoint}
            configTimeParametersByCheckpoint={configTimeParametersByCheckpoint}
          />
        </div>
        <div className="w-full flex gap-2 [&_button]:flex [&_button]:w-full [&_button]:items-center [&_button]:justify-center [&_div]:w-full">
          <ButtonField>
            <button disabled={isLoading || !isValid || isPending} type="submit">
              {isPending ? (
                <IconLoader2 className="animate-spin" size={16} />
              ) : (
                'Run Evaluation'
              )}
            </button>
          </ButtonField>
        </div>
      </form>
    </Smokebreak>
  );
};

export default RunEvaluation;
